/* eslint-disable indent */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, createRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import moment from 'moment'
import { FormControl, FormHelperText, Tab, Tooltip } from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import TabList from '@mui/lab/TabList'
import { TabContext, TabPanel } from '@mui/lab'

import { attributesNames } from '../../../constants'
import { checkErrorResponse, DebugView, isDebug, Logger, LoggerTypes } from '../../../utils'
import {
  FormTitle,
  buttonTypes,
  buttonModes,
  iconNames,
  Icon,
  // == RESOURCE
  AttributeResourceDialogCreate,
  AttributeResourceDialogUpdate,
  // == VARIABLES
  AttributeVariableDialogCreate,
  AttributeVariableDialogUpdate,
  // == PRODUCTS
  AttributeProductDialogCreate,
  AttributeProductDialogUpdate,
  // == LOCATIONS
  AttributeLocationDialogCreate,
  AttributeLocationDialogUpdate,
  // == WEATHERS
  AttributeWeatherDialogCreate,
  AttributeWeatherDialogUpdate,
  // == PARAMETERS
  AttributeParameterDialogCreate,
  AttributeParameterDialogUpdate,
  Button
} from '../../../components'
import {
  // == RESOURCES
  getGlobalResources,
  createGlobalResource,
  updateGlobalResource,
  deleteGlobalResource,

  // == VARIABLES
  getGlobalVariables,
  createGlobalVariable,
  updateGlobalVariable,
  deleteGlobalVariable,

  // == PRODUCTS
  getGlobalProducts,
  createGlobalProduct,
  updateGlobalProduct,
  deleteGlobalProduct,

  // == LOCATIONS
  getGlobalLocations,
  createGlobalLocation,
  updateGlobalLocation,
  deleteGlobalLocation,

  // == WEATHERS
  getGlobalWeathers,
  createGlobalWeather,
  updateGlobalWeather,
  deleteGlobalWeather,

  // == PARAMETERS
  getGlobalParameters,
  createGlobalParameter,
  updateGlobalParameter,
  deleteGlobalParameter,
  downloadGlobalWeather
} from '../../../actions'
import { GlobalAttributeList } from '../GlobalAttributeList'
import './style.scss'

let numLoaded = 0

export const GlobalAttributeTabs = ({
  currentProject,
  currentScenario,
  globalResources,
  globalVariables,
  globalProducts,
  globalLocations,
  globalWeathers,
  globalParameters,
  tabName,
  setTabValue,
  tabVisible,
  selectedAttributes,
  onGlobalsImport
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const ref = createRef()

  /**
   * Convert the injected current `tabName` (can be one of: 'weather_data', 'locations', 'resources', 'products', 'variables', 'parameters')
   * into the `tabValue` (can be one of: 0, 1, 2, 3, 4, 5).
   * Only if the current tabVisible is actually visible.
   *
   * Ex: tabName='weather_data' with tabVisible={weather_data: true} >> tabValue=0
   **/
  let tabValue = 0
  let tabPosition = 0
  let tabIds = {}
  for (const [_tabName, isVisible] of Object.entries(tabVisible)) {
    if (isVisible === true) {
      if (_tabName === tabName) {
        tabValue = tabPosition
      }
      tabIds[_tabName] = tabPosition
      tabPosition++
    }
  }

  // -- if only one tab is visible => hide the top tab selector
  const isTopTabListVisible = tabPosition > 1

  // -- fit the size of the tabs accordingly to the number of visible ones.
  const tabSize = `${Math.floor(100 / tabPosition)}%`

  const [value, setValue] = useState(tabValue.toString())
  const [openDialogResource, setDialogResourceOpen] = useState(false)
  const [openDialogProduct, setDialogProductOpen] = useState(false)
  const [openDialogVariable, setDialogVariableOpen] = useState(false)
  const [openDialogWeather, setDialogWeatherOpen] = useState(false)
  const [openDialogParameter, setDialogParameterOpen] = useState(false)
  const [openDialogLocation, setDialogLocationOpen] = useState(false)
  const [errors, setErrors] = useState(null)

  const [isLoading, setIsLoading] = useState(true)

  const _onDispatch = ({ func, funcName }) => {
    setErrors(null)

    return dispatch(func)
      .then((res) => {
        Logger.trace(LoggerTypes.CONTROLLER, `GlobalAttributeTabs._onDispatch().dispatch(${funcName}).then()`, { res })
        if (res?.data?.isAxiosError) {
          enqueueSnackbar(t('common.api_failure'), { variant: 'error' })
        }
      })
      .catch((err) => {
        Logger.trace(LoggerTypes.WARN, `GlobalAttributeTabs._onDispatch().dispatch(${funcName}).catch()`, { err })

        checkErrorResponse(err, enqueueSnackbar, t)

        setErrors(err?.response?.data?.error || null)
      })
  }

  // == RESOURCES
  const resourceItems = (globalResources && globalResources.items) || []
  /**
   * Adding a `non_working_period` column using `non_working_period_start` - `non_working_period_end`
   */
  // resourceItems.forEach((el) => {
  //   const non_working_period_start = el.non_working_period_start === null ? '' : el.non_working_period_start
  //   const non_working_period_end = el.non_working_period_end === null ? '' : el.non_working_period_end
  //   el.non_working_period = `${non_working_period_start} - ${non_working_period_end}`
  // })

  const onDialogResourcesOpen = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'GlobalAttributeTabs.onDialogResourcesOpen()')
    setDialogResourceOpen(true)
  }
  /**
   * This extra attribute is propagated to Update Popin within the DataGrid [edit] in each row:
   *   <GlobalAttributeList {...}>
   *     <DataGrid {...}>
   *        <AttributeResourceDialogUpdate {...} actionUpdateDispatch={updateGlobalResource}>
   */
  const attributeResourceUpdate = {
    actionUpdateDispatch: updateGlobalResource
  }
  /**
   * This extra attribute is propagated to Update Popin within the DataGrid [delete] in each row:
   *   <GlobalAttributeList {...}>
   *     <DataGrid {...}>
   *        <DialogDelete {...attributeResourceUpdate}>
   */
  const attributeResourceDelete = {
    title: t('attributes.resources.delete.title'),
    message: t('attributes.resources.delete.description'),
    actionDispatchDelete: (resourceId) => {
      Logger.trace(LoggerTypes.CONTROLLER, 'GlobalAttributeTabs.attributeResourceDelete()', { resourceId })
      _onDispatch({ func: deleteGlobalResource(resourceId), funcName: 'deleteGlobalResource' })
    }
  }
  const columnsResources = [
    {
      field: 'id',
      headerName: 'ID',
      renderCell: (e) => <DebugView type={attributesNames.RESOURCES}>{e.row}</DebugView>,
      width: 220
    },
    {
      field: 'name',
      headerName: t('attributes.resources.columns.name'),
      flex: 1,
      minWidth: 150
    },
    {
      field: 'description',
      headerName: t('attributes.resources.columns.description'),
      flex: 1,
      minWidth: 150
    },
    {
      field: 'quantity',
      headerName: t('attributes.resources.columns.quantity'),
      width: 100
    },
    {
      field: 'worktime_start',
      renderCell: (e) => <span>{moment(`2022-12-25T${e.row.worktime_start}`).format('HH:mm')}</span>,
      headerName: t('attributes.resources.columns.worktime_start'),
      width: 90
    },
    {
      field: 'worktime_end',
      renderCell: (e) => <span>{moment(`2022-12-25T${e.row.worktime_end}`).format('HH:mm')}</span>,
      headerName: t('attributes.resources.columns.worktime_end'),
      width: 90
    },
    {
      field: 'non_working_days',
      headerName: t('attributes.resources.columns.non_working_days'),
      width: 150,
      renderCell: (e) => {
        const _non_working_days = String(e?.row?.non_working_days || '').split(',')
        return _non_working_days.map(
          (day, ind) =>
            day &&
            day.length > 0 && (
              <Tooltip key={`day-${ind}`} ref={ref} title={t(`common.weekdays.${day}`)}>
                <div className="non_working_days">{day.substring(0, 2)}</div>
              </Tooltip>
            )
        )
      }
    },
    {
      field: 'non_working_period',
      headerName: t('attributes.resources.columns.non_working_period'),
      width: 150,
      renderCell: (e) => (
        <div className="non_working_period">
          {e?.row?.non_working_period_start && (
            <div>
              <b>{t('attributes.resources.columns.non_working_period_from')}</b>
              <div className="month">{moment(`${e.row.non_working_period_start}`).format('MMMM, DD')}</div>
              {/* <div className="year">{moment(`${e.row.non_working_period_start}`).format('YYYY')}</div> */}
            </div>
          )}
          {e?.row?.non_working_period_end && (
            <div>
              <b>{t('attributes.resources.columns.non_working_period_to')}</b>
              <div className="month">{moment(`${e.row.non_working_period_end}`).format('MMMM, DD')}</div>
              {/* <div className="year">{moment(`${e.row.non_working_period_end}`).format('YYYY')}</div> */}
            </div>
          )}
        </div>
      )
    }
  ]

  // == VARIABLES
  const variableItems = (globalVariables && globalVariables.items) || []
  const onDialogVariableOpen = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'GlobalAttributeTabs.onDialogVariableOpen()')
    setDialogVariableOpen(true)
  }
  const attributeVariableDelete = {
    title: t('attributes.variables.delete.title'),
    message: t('attributes.variables.delete.description'),
    actionDispatchDelete: (variableId) => {
      Logger.trace(LoggerTypes.CONTROLLER, 'GlobalAttributeTabs.attributeVariableDelete()', { variableId })
      _onDispatch({ func: deleteGlobalVariable(variableId), funcName: 'deleteGlobalVariable' })
    }
  }
  const attributeVariableUpdate = {
    actionUpdateDispatch: updateGlobalVariable
  }
  const columnsVariables = [
    {
      field: 'id',
      headerName: 'ID',
      renderCell: (e) => <DebugView type={attributesNames.VARIABLES}>{e.row}</DebugView>,
      width: 220
    },
    {
      field: 'name',
      headerName: t('attributes.variables.columns.name'),
      flex: 1,
      minWidth: 250
      // renderCell: (e) => <DataGridRowLink ref={ref} tooltip={e.row.description} text={e.formattedValue} />
    },
    {
      field: 'description',
      headerName: t('attributes.variables.columns.description'),
      flex: 1,
      minWidth: 250
    },
    {
      field: 'initial_value',
      headerName: t('attributes.variables.columns.initial_value'),
      width: 150
    },
    {
      field: 'unit',
      headerName: t('attributes.variables.columns.unit'),
      width: 100
    }
  ]

  // == PRODUCTS
  const productItems = (globalProducts && globalProducts.items) || []
  const onProductDialogOpen = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'GlobalAttributeTabs.onProductDialogOpen()')
    setDialogProductOpen(true)
  }
  const attributeProductUpdate = {
    actionUpdateDispatch: updateGlobalProduct
  }
  const attributeProductDelete = {
    title: t('attributes.products.delete.title'),
    message: t('attributes.products.delete.description'),
    actionDispatchDelete: (productId) => {
      Logger.trace(LoggerTypes.CONTROLLER, 'GlobalAttributeTabs.attributeProductDelete()', { productId })
      _onDispatch({ func: deleteGlobalProduct(productId), funcName: 'deleteGlobalProduct' })
    }
  }
  const columnsProducts = [
    {
      field: 'id',
      headerName: 'ID',
      renderCell: (e) => <DebugView type={attributesNames.PRODUCTS}>{e.row}</DebugView>,
      width: 220
    },
    {
      field: 'name',
      headerName: t('attributes.products.columns.name'),
      flex: 1,
      minWidth: 250
    },
    {
      field: 'description',
      headerName: t('attributes.products.columns.description'),
      flex: 1,
      minWidth: 250
    },
    {
      field: 'quantity',
      headerName: t('attributes.products.columns.quantity'),
      width: 100
    },
    {
      field: 'weight',
      headerName: t('attributes.products.columns.weight'),
      width: 100
    },
    {
      field: 'dimensions',
      headerName: t('attributes.products.columns.dimensions'),
      width: 150
    }
  ]

  // == LOCATIONS
  const locationItems = (globalLocations && globalLocations.items) || []
  const onDialogLocationsOpen = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'GlobalAttributeTabs.onDialogLocationsOpen()')
    setDialogLocationOpen(true)
  }
  const attributeLocationUpdate = {
    actionUpdateDispatch: updateGlobalLocation
  }
  const attributeLocationDelete = {
    title: t('attributes.locations.delete.title'),
    message: t('attributes.locations.delete.description'),
    actionDispatchDelete: (locationId) => {
      Logger.trace(LoggerTypes.CONTROLLER, 'GlobalAttributeTabs.attributeLocationDelete()')
      _onDispatch({ func: deleteGlobalLocation(locationId), funcName: 'deleteGlobalLocation' })
    }
  }
  const columnsLocations = [
    {
      field: 'id',
      headerName: 'ID',
      renderCell: (e) => <DebugView type={attributesNames.LOCATIONS}>{e.row}</DebugView>,
      width: 220
    },
    {
      field: 'name',
      headerName: t('attributes.locations.columns.name'),
      flex: 1,
      minWidth: 250
    },
    {
      field: 'description',
      headerName: t('attributes.locations.columns.description'),
      flex: 1,
      minWidth: 250
    },
    {
      field: 'latitude',
      headerName: t('attributes.locations.columns.latitude'),
      width: 150
    },
    {
      field: 'longitude',
      headerName: t('attributes.locations.columns.longitude'),
      width: 100
    }
  ]

  // == WEATHERS
  const weatherItems = (globalWeathers && globalWeathers.items) || []
  const onDialogWeathersOpen = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'GlobalAttributeTabs.onDialogWeathersOpen()')
    setDialogWeatherOpen(true)
  }
  const attributeWeatherUpdate = {
    actionUpdateDispatch: updateGlobalWeather
  }
  const attributeWeatherDelete = {
    title: t('attributes.weather_data.delete.title'),
    message: t('attributes.weather_data.delete.description'),
    actionDispatchDelete: (weatherId) => {
      Logger.trace(LoggerTypes.CONTROLLER, 'GlobalAttributeTabs.attributeWeatherDelete()')
      _onDispatch({ func: deleteGlobalWeather(weatherId), funcName: 'deleteGlobalWeather' })
    }
  }
  const attributeWeatherDownload = (e, weather) => {
    e.stopPropagation()
    e.preventDefault()
    Logger.trace(LoggerTypes.CONTROLLER, 'GlobalAttributeTabs.attributeWeatherDownload()', { e, weather })

    dispatch(downloadGlobalWeather(weather))
      .then((res) => {
        Logger.trace(
          LoggerTypes.CONTROLLER,
          'GlobalAttributeTabs.attributeWeatherDownload().dispatch(downloadGlobalWeather(...)).then()',
          { res }
        )

        if (res?.data?.isAxiosError) {
          enqueueSnackbar(t('common.api_failure'), { variant: 'error' })
        }
      })
      .catch((res) => {
        Logger.trace(
          LoggerTypes.WARN,
          'GlobalAttributeTabs.attributeWeatherDownload().dispatch(downloadGlobalWeather(...)).catch()',
          { res }
        )

        const error = (res && res.response && res.response.data && JSON.stringify(res.response.data)) || ''
        enqueueSnackbar(`${t('common.api_failure')} ${error}`, { variant: 'error' })
      })
  }
  const columnsWeathers = [
    {
      field: 'id',
      headerName: 'ID',
      renderCell: (e) => <DebugView type={attributesNames.WEATHER_DATA}>{e.row}</DebugView>,
      width: 220
    },
    {
      field: 'name',
      headerName: t('attributes.weather_data.columns.name'),
      flex: 1,
      minWidth: 250
    },
    {
      field: 'filename',
      headerName: t('attributes.weather_data.columns.filename'),
      flex: 1,
      minWidth: 370,
      renderCell: (e) => (
        <Button
          mode={buttonModes.PRIMARY_ACTION}
          type={buttonTypes}
          text={e.row.filename}
          tooltip={t('attributes.weather_data.download_tooltip')}
          icon={<FileDownloadIcon />}
          onClick={(_e) => attributeWeatherDownload(_e, e.row)}
          className="my-css-class-name"
        />
      )
    },
    {
      field: 'wind_10y_RP',
      headerName: t('attributes.weather_data.columns.wind_10y_RP'),
      width: 120
    }
  ]

  // == PARAMETERS
  const parameterItems = (globalParameters && globalParameters.items) || []
  const onDialogParametersOpen = () => {
    Logger.trace(LoggerTypes.CONTROLLER, 'GlobalAttributeTabs.onDialogParametersOpen()')
    setDialogParameterOpen(true)
  }
  const attributeParameterUpdate = {
    actionUpdateDispatch: updateGlobalParameter
  }
  const attributeParameterDelete = {
    title: t('attributes.parameters.delete.title'),
    message: t('attributes.parameters.delete.description'),
    actionDispatchDelete: (parameterId) => {
      Logger.trace(LoggerTypes.CONTROLLER, 'GlobalAttributeTabs.attributeParameterDelete()')
      _onDispatch({ func: deleteGlobalParameter(parameterId), funcName: 'deleteGlobalParameter' })
    }
  }
  const columnsParameters = [
    {
      field: 'id',
      headerName: 'ID',
      renderCell: (e) => <DebugView type={attributesNames.PARAMETERS}>{e.row}</DebugView>,
      width: 220
    },
    {
      field: 'name',
      headerName: t('attributes.parameters.columns.name'),
      flex: 1,
      minWidth: 250
    },
    {
      field: 'description',
      headerName: t('attributes.parameters.columns.description'),
      flex: 1,
      minWidth: 250
    },
    {
      field: 'value',
      headerName: t('attributes.parameters.columns.value'),
      width: 150
    },
    {
      field: 'unit',
      headerName: t('attributes.parameters.columns.unit'),
      width: 100
    }
  ]

  // -----------------------------------------------------------------

  const onTabChange = (e, newValue) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'GlobalAttributeTabs.onTabChange()', { e, newValue })

    setValue(newValue)
    setTabValue(parseInt(newValue, 10))
  }

  useEffect(() => {
    const loadData = [
      { dispatch: getGlobalResources(), methodName: 'getGlobalResources', id: attributesNames.RESOURCES },
      { dispatch: getGlobalVariables(), methodName: 'getGlobalVariables', id: attributesNames.VARIABLES },
      { dispatch: getGlobalProducts(), methodName: 'getGlobalProducts', id: attributesNames.PRODUCTS },
      { dispatch: getGlobalLocations(), methodName: 'getGlobalLocations', id: attributesNames.LOCATIONS },
      { dispatch: getGlobalWeathers(), methodName: 'getGlobalWeathers', id: attributesNames.WEATHER_DATA },
      { dispatch: getGlobalParameters(), methodName: 'getGlobalParameters', id: attributesNames.PARAMETERS }
    ]

    loadData.forEach((_current) => {
      if (tabVisible && tabVisible[_current.id] === true) {
        dispatch(_current.dispatch)
          .then((res) => {
            numLoaded++
            Logger.trace(
              LoggerTypes.CONTROLLER,
              `GlobalAttributeTabsDialog.useEffect().dispatch(${_current.methodName}()).then()`,
              {
                currentProject,
                currentScenario,
                numLoaded,
                res
              }
            )

            if (!res || !res.data || res.data.isAxiosError) {
              enqueueSnackbar(t('common.api_failure'), { variant: 'error' })
            }

            if (numLoaded === Object.keys(tabIds).length) {
              // enqueueSnackbar(t('common.api_success'), { variant: 'success' })
              setIsLoading(false)
              numLoaded = 0
            }
          })
          .catch((res) => {
            Logger.trace(
              LoggerTypes.WARN,
              `GlobalAttributeTabsDialog.useEffect().dispatch(${_current.methodName}()).catch()`,
              {
                currentProject,
                currentScenario,
                res
              }
            )
            const error = (res && res.response && res.response.data && JSON.stringify(res.response.data)) || ''
            enqueueSnackbar(`${t('common.api_failure')} ${error}`, { variant: 'error' })
          })
      }
    })
  }, [])

  if (!isLoading) {
    Logger.trace(LoggerTypes.CONTAINER, 'GlobalAttributeTabs.render()', {
      tabVisible,
      tabName,
      tabValue,
      tabIds,
      tabSize,
      len: Object.keys(tabIds).length,
      numLoaded,
      selectedAttributes,
      attributes: {
        resourceItems,
        productItems,
        variableItems,
        locationItems,
        weatherItems,
        parameterItems
      }
    })
  }

  return (
    !isLoading && (
      <div className={'import-tabs'}>
        {isTopTabListVisible === false ? (
          // -- if only one tab is visible display its name at the top
          <FormTitle
            title={t(`attributes.${tabName}.tab_title`)}
            icon={iconNames[String(tabName).toUpperCase()]}
            iconSize={26}
            style={{ fontSize: '20px' }}
          />
        ) : (
          // -- otherwise we display the common Global import title
          <FormTitle
            title={t('global.global_library_title')}
            icon={iconNames.GLOBALS}
            iconSize={26}
            style={{ fontSize: '20px' }}
          />
        )}
        <TabContext value={value}>
          <TabList style={{ display: isTopTabListVisible ? 'block' : 'none' }} onChange={onTabChange}>
            {tabVisible.locations && (
              <Tab
                disableRipple={true}
                label={t('attributes.locations.tab_title')}
                style={{ width: tabSize }}
                value={`${tabIds.locations}`}
                icon={<Icon name={iconNames.LOCATION} size={18} />}
              />
            )}
            {tabVisible.weather_data && (
              <Tab
                disableRipple={true}
                label={t('attributes.weather_data.tab_title')}
                style={{ width: tabSize }}
                value={`${tabIds.weather_data}`}
                icon={<Icon name={iconNames.WEATHER_DATA} size={22} />}
              />
            )}
            {tabVisible.resources && (
              <Tab
                disableRipple={true}
                label={t('attributes.resources.tab_title')}
                style={{ width: tabSize }}
                value={`${tabIds.resources}`}
                icon={<Icon name={iconNames.RESOURCE} size={20} />}
              />
            )}
            {tabVisible.products && (
              <Tab
                disableRipple={true}
                label={t('attributes.products.tab_title')}
                style={{ width: tabSize }}
                value={`${tabIds.products}`}
                icon={<Icon name={iconNames.PRODUCT} size={20} />}
              />
            )}
            {tabVisible.variables && (
              <Tab
                disableRipple={true}
                label={t('attributes.variables.tab_title')}
                style={{ width: tabSize }}
                value={`${tabIds.variables}`}
                icon={<Icon name={iconNames.VARIABLE} size={20} />}
              />
            )}
            {tabVisible.parameters && (
              <Tab
                disableRipple={true}
                label={t('attributes.parameters.tab_title')}
                style={{ width: tabSize }}
                value={`${tabIds.parameters}`}
                icon={<Icon name={iconNames.PARAMETER} size={18} />}
              />
            )}
          </TabList>

          {
            //  LOCATIONS
            tabVisible.locations && (
              <TabPanel value={`${tabIds.locations}`}>
                <GlobalAttributeList
                  attributeName={attributesNames.LOCATIONS}
                  items={locationItems}
                  columns={columnsLocations}
                  columnVisibilityModel={{ id: isDebug() }}
                  extraDialogAttributeDelete={attributeLocationDelete}
                  extraDialogAttributeUpdate={attributeLocationUpdate}
                  DialogUpdate={AttributeLocationDialogUpdate}
                  onOpenDialog={onDialogLocationsOpen}
                  // selectListGlobal={selectListGlobalLocations}
                  // selectedAttribute={selectedAttributes?.locations || null}
                  onGlobalsImport={onGlobalsImport}
                />
                {openDialogLocation && (
                  <AttributeLocationDialogCreate
                    // currentProject={currentProject}
                    // currentScenario={currentScenario}
                    openDialog={openDialogLocation}
                    setOpenDialog={setDialogLocationOpen}
                    actionCreateDispatch={createGlobalLocation}
                  />
                )}
              </TabPanel>
            )
          }

          {
            /* WEATHERS  */
            tabVisible.weather_data && (
              <TabPanel value={`${tabIds.weather_data}`}>
                <GlobalAttributeList
                  attributeName={attributesNames.WEATHER_DATA}
                  items={weatherItems}
                  columns={columnsWeathers}
                  columnVisibilityModel={{ id: isDebug() }}
                  extraDialogAttributeDelete={attributeWeatherDelete}
                  extraDialogAttributeUpdate={attributeWeatherUpdate}
                  DialogUpdate={AttributeWeatherDialogUpdate}
                  onOpenDialog={onDialogWeathersOpen}
                  // importGlobal={globalImportWeathers}
                  // selectListGlobal={selectListGlobalWeathers}
                  selectedAttribute={selectedAttributes?.weather_data || null}
                  onGlobalsImport={onGlobalsImport}
                />
                {openDialogWeather && (
                  <AttributeWeatherDialogCreate
                    // currentProject={currentProject}
                    // currentScenario={currentScenario}
                    openDialog={openDialogWeather}
                    setOpenDialog={setDialogWeatherOpen}
                    actionCreateDispatch={createGlobalWeather}
                  />
                )}
              </TabPanel>
            )
          }

          {
            /*  RESOURCES  */
            tabVisible.resources && (
              <TabPanel value={`${tabIds.resources}`}>
                <GlobalAttributeList
                  attributeName={attributesNames.RESOURCES}
                  items={resourceItems}
                  columns={columnsResources}
                  columnVisibilityModel={{ id: isDebug() }}
                  extraDialogAttributeDelete={attributeResourceDelete}
                  extraDialogAttributeUpdate={attributeResourceUpdate}
                  DialogUpdate={AttributeResourceDialogUpdate}
                  onOpenDialog={onDialogResourcesOpen}
                  selectedAttribute={selectedAttributes?.resources || null}
                  onGlobalsImport={onGlobalsImport}
                />
                {openDialogResource && (
                  <AttributeResourceDialogCreate
                    // currentProject={currentProject}
                    // currentScenario={currentScenario}
                    parameters={parameterItems}
                    openDialog={openDialogResource}
                    setOpenDialog={setDialogResourceOpen}
                    actionCreateDispatch={createGlobalResource}
                  />
                )}
              </TabPanel>
            )
          }

          {
            /*  PRODUCTS  */
            tabVisible.products && (
              <TabPanel value={`${tabIds.products}`}>
                <GlobalAttributeList
                  attributeName={attributesNames.PRODUCTS}
                  items={productItems}
                  columns={columnsProducts}
                  columnVisibilityModel={{ id: isDebug() }}
                  extraDialogAttributeDelete={attributeProductDelete}
                  extraDialogAttributeUpdate={attributeProductUpdate}
                  DialogUpdate={AttributeProductDialogUpdate}
                  onOpenDialog={onProductDialogOpen}
                  selectedAttribute={selectedAttributes?.products || null}
                  onGlobalsImport={onGlobalsImport}
                />
                {openDialogProduct && (
                  <AttributeProductDialogCreate
                    // currentProject={currentProject}
                    // currentScenario={currentScenario}
                    openDialog={openDialogProduct}
                    setOpenDialog={setDialogProductOpen}
                    actionCreateDispatch={createGlobalProduct}
                  />
                )}
              </TabPanel>
            )
          }

          {
            /*  VARIABLES  */
            tabVisible.variables && (
              <TabPanel value={`${tabIds.variables}`}>
                <GlobalAttributeList
                  attributeName={attributesNames.VARIABLES}
                  items={variableItems}
                  columns={columnsVariables}
                  columnVisibilityModel={{ id: isDebug() }}
                  extraDialogAttributeDelete={attributeVariableDelete}
                  extraDialogAttributeUpdate={attributeVariableUpdate}
                  DialogUpdate={AttributeVariableDialogUpdate}
                  onOpenDialog={onDialogVariableOpen}
                  selectedAttribute={selectedAttributes?.variables || null}
                  onGlobalsImport={onGlobalsImport}
                />
                {openDialogVariable && (
                  <AttributeVariableDialogCreate
                    // currentProject={currentProject}
                    // currentScenario={currentScenario}
                    openDialog={openDialogVariable}
                    setOpenDialog={setDialogVariableOpen}
                    actionCreateDispatch={createGlobalVariable}
                  />
                )}
              </TabPanel>
            )
          }

          {
            /*  PARAMETERS */
            tabVisible.parameters && (
              <TabPanel value={`${tabIds.parameters}`}>
                <GlobalAttributeList
                  attributeName={attributesNames.PARAMETERS}
                  items={parameterItems}
                  columns={columnsParameters}
                  columnVisibilityModel={{ id: isDebug() }}
                  extraDialogAttributeDelete={attributeParameterDelete}
                  extraDialogAttributeUpdate={attributeParameterUpdate}
                  DialogUpdate={AttributeParameterDialogUpdate}
                  onOpenDialog={onDialogParametersOpen}
                  selectedAttribute={selectedAttributes?.parameters || null}
                  onGlobalsImport={onGlobalsImport}
                />
                {openDialogParameter && (
                  <AttributeParameterDialogCreate
                    // currentProject={currentProject}
                    // currentScenario={currentScenario}
                    parameters={parameterItems}
                    openDialog={openDialogParameter}
                    setOpenDialog={setDialogParameterOpen}
                    actionCreateDispatch={createGlobalParameter}
                  />
                )}
              </TabPanel>
            )
          }
        </TabContext>

        {/* Error handler */}
        {errors && (
          <FormControl error={Boolean(errors)} className="common_fullwidth">
            <FormHelperText>{errors || ''}</FormHelperText>
          </FormControl>
        )}
      </div>
    )
  )
}

GlobalAttributeTabs.displayName = 'GlobalAttributeTabs'

GlobalAttributeTabs.propTypes = {
  currentProject: PropTypes.object,
  currentScenario: PropTypes.object,
  globalResources: PropTypes.object,
  globalVariables: PropTypes.object,
  globalProducts: PropTypes.object,
  globalLocations: PropTypes.object,
  globalWeathers: PropTypes.object,
  globalParameters: PropTypes.object,
  tabName: PropTypes.oneOf(['weather_data', 'locations', 'resources', 'products', 'variables', 'parameters']),
  setTabValue: PropTypes.func.isRequired,
  tabVisible: PropTypes.shape({
    variables: PropTypes.bool,
    products: PropTypes.bool,
    resources: PropTypes.bool,
    locations: PropTypes.bool,
    weather_data: PropTypes.bool,
    parameters: PropTypes.bool
  }),
  selectedAttributes: PropTypes.shape({
    variables: PropTypes.arrayOf(PropTypes.object),
    products: PropTypes.arrayOf(PropTypes.object),
    resources: PropTypes.arrayOf(PropTypes.object),
    locations: PropTypes.arrayOf(PropTypes.object),
    weather_data: PropTypes.arrayOf(PropTypes.object),
    parameters: PropTypes.arrayOf(PropTypes.object)
  }),
  onGlobalsImport: PropTypes.func
}

GlobalAttributeTabs.defaultProps = {
  currentProject: null,
  currentScenario: null,
  globalResources: null,
  globalVariables: null,
  globalProducts: null,
  globalLocations: null,
  globalWeathers: null,
  globalParameters: null,
  tabName: 'weather',
  setTabValue: () => {},
  tabVisible: {
    variables: true,
    products: true,
    resources: true,
    locations: true,
    weather_data: true,
    parameters: true
  },
  selectedAttributes: null,
  onGlobalsImport: () => {}
}
